import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNotification } from "../../contexts/NotificationContext";
import { apiService } from "../../backend/api";
import { getBaseUrl } from "../../config/apiConfig";
import { Plus, Pencil, Trash2, Euro, Download } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card } from "../../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";

const AdminPricing = () => {
  const { showNotification, confirm } = useNotification();
  const { t } = useTranslation();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    downloads: 0,
    price: 0,
    type: "subscription",
    icon: "Package",
    features: "",
    missingFeatures: "",
    highlightFeatures: "",
    priceLabel: "",
    savingsLabel: "",
    description: "",
    isActive: true
  });

  // Referenzpreis: höchster Pro-Download-Preis aus One-Time-Paketen (= Einzelkauf)
  const oneTimePlans = plans.filter(p => p.type === 'one-time' && p.downloads > 0);
  const referencePrice = oneTimePlans.length > 0
    ? Math.max(...oneTimePlans.map(p => p.price / p.downloads))
    : 0;

  // Auto-berechnete Werte für Preis-Label und Rabatt-Label
  const calcPricePerDownload = formData.downloads > 0 && formData.price > 0
    ? formData.price / formData.downloads
    : 0;
  const calcPriceLabel = calcPricePerDownload > 0
    ? `${calcPricePerDownload.toFixed(2).replace('.', ',')}€ / Download`
    : '';
  const calcSavingsPercent = calcPricePerDownload > 0 && referencePrice > 0 && calcPricePerDownload < referencePrice
    ? Math.round((1 - calcPricePerDownload / referencePrice) * 100)
    : 0;
  const calcSavingsLabel = calcSavingsPercent > 0 ? `${calcSavingsPercent}% Rabatt` : '';

  // State für Lösch-Bestätigung (Name eintippen)
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const data = await apiService.request(`${getBaseUrl()}/api/admin/plans`);
      setPlans(data);
    } catch (err) {
      setError(t("adminPricing.errorLoadingPlans"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  const handleOpenModal = (plan = null) => {
    if (plan) {
      setEditingPlan(plan);
      setFormData({
        name: plan.name,
        downloads: plan.downloads,
        price: plan.price,
        type: plan.type,
        icon: plan.icon || "Package",
        features: plan.features ? plan.features.join("\n") : "",
        missingFeatures: plan.missingFeatures ? plan.missingFeatures.join("\n") : "",
        highlightFeatures: plan.highlightFeatures ? plan.highlightFeatures.join("\n") : "",
        priceLabel: plan.priceLabel || "",
        savingsLabel: plan.savingsLabel || "",
        description: plan.description || "",
        isActive: plan.isActive
      });
    } else {
      setEditingPlan(null);
      setFormData({
        name: "",
        downloads: 0,
        price: 0,
        type: "subscription",
        icon: "Package",
        features: "",
        missingFeatures: "",
        highlightFeatures: "",
        priceLabel: "",
        savingsLabel: "",
        description: "",
        isActive: true
      });
    }
    setIsModalOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        ...formData,
        // Immer automatisch berechnen – manuelle Eingabe wird ignoriert
        priceLabel: formData.downloads > 0 ? calcPriceLabel : '',
        savingsLabel: formData.downloads > 0 ? calcSavingsLabel : '',
        features: formData.features.split("\n").filter(f => f.trim() !== ""),
        missingFeatures: formData.missingFeatures.split("\n").filter(f => f.trim() !== ""),
        highlightFeatures: formData.highlightFeatures.split("\n").filter(f => f.trim() !== "")
      };
      if (editingPlan) {
        await apiService.request(`${getBaseUrl()}/api/admin/plans/${editingPlan._id}`, {
          method: "PUT",
          body: JSON.stringify(payload)
        });
      } else {
        await apiService.request(`${getBaseUrl()}/api/admin/plans`, {
          method: "POST",
          body: JSON.stringify(payload)
        });
      }
      setIsModalOpen(false);
      fetchPlans();
    } catch (err) {
      showNotification(t("adminPricing.errorSavingPlan") + err.message, "error");
    }
  };

  const handleDeleteRequest = (plan) => {
    setDeleteTarget(plan);
    setDeleteConfirmName('');
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget || deleteConfirmName !== deleteTarget.name) return;
    try {
      await apiService.request(`${getBaseUrl()}/api/admin/plans/${deleteTarget._id}`, {
        method: "DELETE"
      });
      fetchPlans();
      setDeleteTarget(null);
      setDeleteConfirmName('');
    } catch (err) {
      showNotification(t("adminPricing.errorDeletingPlan"), "error");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white">{t("adminPricing.pageTitle")}</h2>
          <p className="text-slate-500 dark:text-slate-400">{t("adminPricing.pageSubtitle")}</p>
        </div>
        <Button onClick={() => handleOpenModal()} className="rounded-xl">
          <Plus className="mr-2" size={18} />
          {t("adminPricing.newPlan")}
        </Button>
      </div>

      <Card className="rounded-[2rem] border-border shadow-sm overflow-hidden bg-white dark:bg-slate-800">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 dark:bg-slate-700">
              <TableHead className="px-6 py-4">{t("adminPricing.tableName")}</TableHead>
              <TableHead className="px-6 py-4">{t("adminPricing.tableType")}</TableHead>
              <TableHead className="px-6 py-4">{t("adminPricing.tableDownloads")}</TableHead>
              <TableHead className="px-6 py-4">{t("adminPricing.tablePrice")}</TableHead>
              <TableHead className="px-6 py-4">€/Download</TableHead>
              <TableHead className="px-6 py-4">Rabatt</TableHead>
              <TableHead className="px-6 py-4">{t("adminPricing.tableStatus")}</TableHead>
              <TableHead className="px-6 py-4 text-right">{t("adminPricing.tableActions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {plans.map((plan) => (
              <TableRow key={plan._id} className="hover:bg-slate-50/50 dark:hover:bg-slate-700/50 transition-colors">
                <TableCell className="px-6 py-4 font-bold text-slate-800 dark:text-white">{plan.name}</TableCell>
                <TableCell className="px-6 py-4">
                  <Badge variant={plan.type === 'subscription' ? 'default' : 'secondary'}>
                    {plan.type === 'subscription' ? t("adminPricing.subscription") : t("adminPricing.oneTimePackage")}
                  </Badge>
                </TableCell>
                <TableCell className="px-6 py-4 font-medium">
                  <div className="flex items-center text-slate-600 dark:text-slate-300">
                    <Download size={14} className="mr-1.5 text-[#0f7699] dark:text-[#4fc3f7] opacity-70" />
                    {plan.downloads}
                  </div>
                </TableCell>
                <TableCell className="px-6 py-4 font-bold text-slate-800 dark:text-white">
                  <div className="flex items-center">
                    <Euro size={14} className="mr-1 text-[#0f7699] dark:text-[#4fc3f7]" />
                    {plan.price.toFixed(2)}
                  </div>
                </TableCell>
                <TableCell className="px-6 py-4 text-slate-600 dark:text-slate-300">
                  {plan.downloads > 0 ? `${(plan.price / plan.downloads).toFixed(2)}€` : '–'}
                </TableCell>
                <TableCell className="px-6 py-4">
                  {(() => {
                    if (plan.downloads <= 0 || referencePrice <= 0) return '–';
                    const ppd = plan.price / plan.downloads;
                    if (ppd >= referencePrice) return '–';
                    return <Badge className="bg-green-50 text-green-600 border-green-100">{Math.round((1 - ppd / referencePrice) * 100)}%</Badge>;
                  })()}
                </TableCell>
                <TableCell className="px-6 py-4">
                  <Badge className={plan.isActive ? "bg-green-50 text-green-600 border-green-100" : "bg-red-50 text-red-600 border-red-100"}>
                    {plan.isActive ? t("adminPricing.active") : t("adminPricing.inactive")}
                  </Badge>
                </TableCell>
                <TableCell className="px-6 py-4 text-right">
                  <div className="flex justify-end space-x-2">
                    <Button variant="ghost" size="icon" onClick={() => handleOpenModal(plan)} className="h-9 w-9 rounded-lg text-slate-400 hover:text-primary">
                      <Pencil size={18} />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDeleteRequest(plan)} className="h-9 w-9 rounded-lg text-slate-400 hover:text-destructive">
                      <Trash2 size={18} />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* Lösch-Bestätigung: Plan-Name eintippen */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) { setDeleteTarget(null); setDeleteConfirmName(''); } }}>
        <DialogContent className="max-w-sm rounded-[2rem]">
          <DialogHeader>
            <DialogTitle className="text-destructive">Plan löschen</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-600 dark:text-slate-300">
              Um <span className="font-bold text-slate-800 dark:text-white">"{deleteTarget?.name}"</span> zu löschen, geben Sie den Plan-Namen zur Bestätigung ein:
            </p>
            <Input
              value={deleteConfirmName}
              onChange={(e) => setDeleteConfirmName(e.target.value)}
              placeholder={deleteTarget?.name}
              className={deleteConfirmName === deleteTarget?.name ? 'border-destructive' : ''}
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setDeleteTarget(null); setDeleteConfirmName(''); }}>Abbrechen</Button>
            <Button
              variant="destructive"
              disabled={deleteConfirmName !== deleteTarget?.name}
              onClick={handleDeleteConfirm}
            >
              Endgültig löschen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-lg rounded-[2rem] max-h-[90vh] flex flex-col">
          <DialogHeader className="shrink-0">
            <DialogTitle>{editingPlan ? t("adminPricing.editPlan") : t("adminPricing.createNewPlan")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 overflow-y-auto flex-1 min-h-0 px-1">
            <div className="space-y-2">
              <label className="text-xs font-bold uppercase text-[#0f7699] dark:text-[#4fc3f7] opacity-70">{t("adminPricing.labelName")}</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t("adminPricing.placeholderName")}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase text-[#0f7699] dark:text-[#4fc3f7] opacity-70">{t("adminPricing.labelType")}</label>
                <select
                  className="w-full h-10 px-3 bg-white dark:bg-slate-700 border dark:border-slate-600 rounded-md text-sm text-slate-800 dark:text-white"
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                >
                  <option value="subscription">{t("adminPricing.subscription")}</option>
                  <option value="one-time">{t("adminPricing.oneTimePackage")}</option>
                </select>
              </div>
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase text-[#0f7699] dark:text-[#4fc3f7] opacity-70">{t("adminPricing.labelDownloads")}</label>
                <Input
                  type="number"
                  value={formData.downloads}
                  onChange={(e) => setFormData({ ...formData, downloads: parseInt(e.target.value) })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase text-[#0f7699] dark:text-[#4fc3f7] opacity-70">{t("adminPricing.labelPrice")}</label>
                <div className="relative">
                  <Euro className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0f7699] dark:text-[#4fc3f7]" size={16} />
                  <Input
                    type="number"
                    step="0.01"
                    className="pl-9"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase text-[#0f7699] dark:text-[#4fc3f7] opacity-70">{t("adminPricing.labelIcon")}</label>
                <Input
                  value={formData.icon}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                  placeholder={t("adminPricing.placeholderIcon")}
                />
              </div>
            </div>
            {/* Auto-berechnete Werte (nur Anzeige) */}
            {formData.downloads > 0 && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-xs font-bold uppercase text-[#0f7699] dark:text-[#4fc3f7] opacity-70">€/Download</label>
                  <div className="h-10 px-3 flex items-center bg-slate-50 dark:bg-slate-700/50 border rounded-md text-sm font-semibold text-slate-700 dark:text-slate-200">
                    {calcPriceLabel || '–'}
                  </div>
                  <p className="text-[10px] text-muted-foreground">Wird automatisch berechnet</p>
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold uppercase text-[#0f7699] dark:text-[#4fc3f7] opacity-70">Rabatt</label>
                  <div className="h-10 px-3 flex items-center bg-slate-50 dark:bg-slate-700/50 border rounded-md text-sm font-semibold text-slate-700 dark:text-slate-200">
                    {calcSavingsLabel || '–'}
                  </div>
                  <p className="text-[10px] text-muted-foreground">Berechnet ggü. Einzelkauf-Preis ({referencePrice > 0 ? `${referencePrice.toFixed(2).replace('.', ',')}€` : '–'})</p>
                </div>
              </div>
            )}
            <div className="space-y-2">
              <label className="text-xs font-bold uppercase text-[#0f7699] dark:text-[#4fc3f7] opacity-70">{t("adminPricing.labelHighlightFeatures")}</label>
              <textarea
                className="w-full p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/30 rounded-md text-sm text-slate-800 dark:text-white min-h-[60px]"
                value={formData.highlightFeatures}
                onChange={(e) => setFormData({ ...formData, highlightFeatures: e.target.value })}
                placeholder={t("adminPricing.placeholderHighlightFeatures")}
              />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-bold uppercase text-[#0f7699] dark:text-[#4fc3f7] opacity-70">{t("adminPricing.labelIncludedFeatures")}</label>
              <textarea
                className="w-full p-3 bg-white dark:bg-slate-700 border dark:border-slate-600 rounded-md text-sm text-slate-800 dark:text-white min-h-[80px]"
                value={formData.features}
                onChange={(e) => setFormData({ ...formData, features: e.target.value })}
                placeholder={t("adminPricing.placeholderIncludedFeatures")}
              />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-bold uppercase text-[#0f7699] dark:text-[#4fc3f7] opacity-70">{t("adminPricing.labelMissingFeatures")}</label>
              <textarea
                className="w-full p-3 bg-white dark:bg-slate-700 border dark:border-slate-600 rounded-md text-sm text-slate-800 dark:text-white min-h-[80px]"
                value={formData.missingFeatures}
                onChange={(e) => setFormData({ ...formData, missingFeatures: e.target.value })}
                placeholder={t("adminPricing.placeholderMissingFeatures")}
              />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-bold uppercase text-[#0f7699] dark:text-[#4fc3f7] opacity-70">{t("adminPricing.labelDescription")}</label>
              <textarea
                className="w-full p-3 bg-white dark:bg-slate-700 border dark:border-slate-600 rounded-md text-sm text-slate-800 dark:text-white min-h-[60px]"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder={t("adminPricing.placeholderDescription")}
              />
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="isActive"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              />
              <label htmlFor="isActive" className="text-sm font-medium text-slate-700 dark:text-slate-200 underline underline-offset-4 decoration-dotted">{t("adminPricing.planIsActive")}</label>
            </div>
          </div>
          <DialogFooter className="shrink-0">
            <Button variant="ghost" onClick={() => setIsModalOpen(false)}>{t("adminPricing.cancel")}</Button>
            <Button onClick={handleSubmit}>{editingPlan ? t("adminPricing.save") : t("adminPricing.create")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPricing;
